import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { GraduationCap, FolderOpen } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Course {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  thumbnail_url?: string | null;
  lessonsCount?: number;
  enrollment?: {
    status: string | null;
  } | null;
}

interface TrainingProgressSectionProps {
  courses: Course[];
}

export function TrainingProgressSection({ courses }: TrainingProgressSectionProps) {
  return (
    <div className="bg-white rounded-2xl sm:rounded-3xl lg:rounded-[40px] border border-slate-200 p-5 sm:p-6 lg:p-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
        <div className="flex items-center gap-3">
          <GraduationCap className="h-5 w-5 sm:h-6 sm:w-6 text-slate-700" />
          <h3 className="text-xl sm:text-2xl text-slate-900">Training Progress</h3>
        </div>
        <Link to="/genius/courses">
          <Button variant="outline" className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3.5 rounded-[48px] border-2 border-slate-200 bg-white text-slate-900 font-normal text-sm sm:text-base hover:bg-slate-50 transition-colors">
            Continue Training
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        {courses?.slice(0, 2).map((course) => (
          <Link key={course.id} to={`/genius/course/${course.slug}`}>
            <div className="bg-white rounded-2xl sm:rounded-3xl lg:rounded-[40px] border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
              <div
                className="h-[140px] sm:h-[180px] flex items-center justify-center"
                style={{ background: 'linear-gradient(180deg, rgba(232, 240, 254, 1) 0%, rgba(240, 245, 255, 1) 50%, rgba(232, 240, 254, 1) 100%)' }}
              >
                {course.thumbnail_url ? (
                  <img src={course.thumbnail_url} alt={course.title} className="w-full h-full object-cover" />
                ) : (
                  <GraduationCap className="h-10 w-10 sm:h-12 sm:w-12 text-[#4669ed]" />
                )}
              </div>
              <div className="p-4 sm:p-6">
                <h4 className="text-lg font-bold text-slate-900 mb-2">{course.title}</h4>
                <p className="text-slate-500 text-sm leading-relaxed mb-4 line-clamp-3">
                  {course.description}
                </p>
                <div className="flex items-center gap-6 text-slate-600 text-sm">
                  <div className="flex items-center gap-2">
                    <FolderOpen className="h-5 w-5" />
                    <span>{course.lessonsCount || 0} lessons</span>
                  </div>
                  {course.enrollment && (
                    <Badge className={cn(
                      "rounded-full px-2.5 py-1 text-[11px] font-bold uppercase",
                      course.enrollment.status === 'completed' ? "bg-emerald-100 text-emerald-800" :
                      "bg-blue-100 text-blue-800"
                    )}>
                      {course.enrollment.status === 'completed' ? 'Completed' : 'In Progress'}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
